import type { Component as ComponentType } from 'vue';
import { h, useSlots, useAttrs, onMounted, onUpdated, ref, createApp, defineComponent, shallowRef } from 'vue';
import { merge } from 'ts-deepmerge';
export { type Component as TraditionComponent } from './types';

const auiConfig = (function () {
    try {
        return __AUI_CONFIG__;
    } catch (e) {
        return { tagPrefix: 'aui' };
    }
})();

const TAG_PREFIX = auiConfig.tagPrefix || 'aui';
const AUI_ATTR_FLAG = 'data-v-aui';
const AUI_EXTEND_ATTR = 'auix';

const shadowSymbol = Symbol('shadow');

export type HookHandler = (c: any) => void | any;

type AUI_COMPONENT_OPTION = {
    valueType?: any
}

export type Component = ComponentType;

type AUI_SLOT_CACHE = {
    start: Comment,
    end: Comment,
};

type AUI_COMP_SHADOW = {
    el?: HTMLElement | null,
    ref?: Component | null,
    value?: string | null,
    options?: AUI_COMPONENT_OPTION,
    needHook: boolean,
    slot: Record<string, AUI_SLOT_CACHE>,
    slotCollection?: Record<string, any[]>,
};

type GlobalAuiCore = {
    defined: Record<string, AUI_VUE_DEFINE_CACHE>
}

type SlotElement = {
    default: Node[],
    [key: string]: Node[],
}

// function getGlobalAuiCore(): GlobalAuiCore {
//     if (!Object.prototype.hasOwnProperty.call(window, '__GLOBAL_AUI_CORE__')) {
//         Object.defineProperty(window, '__GLOBAL_AUI_CORE__', {
//             value: {
//                 defined: {} as Record<string, AUI_VUE_DEFINE_CACHE>
//             },
//             writable: false,
//             enumerable: false,
//         });
//     }
//     return (window as any)['__GLOBAL_AUI_CORE__'];
// }


export class AuiComponent extends HTMLElement {
    __hasVue__ = false;
    private static readonly shadowSymbol: typeof shadowSymbol = shadowSymbol;
    private [shadowSymbol]: AUI_COMP_SHADOW = { needHook: false, slot: {} };

    constructor(options?: AUI_COMPONENT_OPTION) {
        super();
        this[AuiComponent.shadowSymbol].options = options || {};
        this.$initAttrs();
    }

    connectedCallback() {
        // console.log('aui component connected:', this.tagName);

        if (this.$needInit()) {
            this.$initVue();
            this.$needHook(true);
        }

    }



    set innerHTML(v: string) {
        if (!this.$needHook()) {
            // super.innerHTML = v;
            Reflect.set(HTMLElement.prototype, 'innerHTML', v, this);
            return;
        }

        this.$innerHTML('default', v);
    }

    get innerHTML() {
        if (!this.$needHook()) {
            // return super.innerHTML || '';
            return Reflect.get(HTMLElement.prototype, 'innerHTML', this) || '';
        }

        return this.$innerHTML('default');
    }

    set textContent(v: string) {
        if (!this.$needHook()) {
            // super.innerHTML = v;
            Reflect.set(HTMLElement.prototype, 'innerHTML', v, this);
            return;
        }

        this.$textContent('default', v);
    }

    get textContent() {
        if (!this.$needHook()) {
            // return super.innerHTML || '';
            return Reflect.get(HTMLElement.prototype, 'innerHTML', this) || '';
        }

        return this.$textContent('default');
    }

    set innerText(v: string) {
        this.$ignoreLog('innerText');
        // super.innerText = v;
        Reflect.set(HTMLElement.prototype, 'innerText', v, this);
    }

    set value(v: string | null) {
        let val: any = v;

        const oldValue = this[AuiComponent.shadowSymbol].value;
        let newValue;

        if (this.$isArrayValue()) {
            val = AuiComponent.$anyToArray(v);
            newValue = JSON.stringify(val);
        } else {
            newValue = val.toString();
        }

        if (oldValue === newValue) return;

        this[AuiComponent.shadowSymbol].value = newValue;

        if (this[AuiComponent.shadowSymbol].el) {
            this.$attrChange('value');
        }
    }

    get value(): any {
        const v = this[AuiComponent.shadowSymbol].value;
        if (this.$isArrayValue()) {
            return typeof v === 'string' ? JSON.parse(v) : v;
        } else {
            return v;
        }
    }

    prepend<T extends Node>(node: T) {
        if (!this.$needHook()) {
            return HTMLElement.prototype.prepend.call(this, node);
        }
        this.$prepend('default', node);
    }

    appendChild<T extends Node>(node: T): T {
        if (!this.$needHook()) {
            return HTMLElement.prototype.appendChild.call(this, node) as T;
        }
        return this.$appendChild('default', node);
    }

    insertBefore<T extends Node>(node: T, child: Node | null): T {
        if (!this.$needHook()) {
            return HTMLElement.prototype.insertBefore.call(this, node, child) as T;
        }
        return this.$insertBefore('default', node, child);
    }

    hasAttribute(qualifiedName: string): boolean {
        const shadowElement = this[AuiComponent.shadowSymbol].el;
        if (shadowElement) {
            return shadowElement.hasAttribute(qualifiedName);
        }
        return HTMLElement.prototype.hasAttribute.call(this, qualifiedName);
    }

    setAttribute(qualifiedName: string, value: string): void {
        value = value.toString();
        const shadowElement = this[AuiComponent.shadowSymbol].el;
        if (shadowElement) {
            if (shadowElement.getAttribute(qualifiedName) === value) return;
            shadowElement.setAttribute(qualifiedName, value);
        }
        const isShow = this.$isShowProp(_M.vueAttrName(qualifiedName));
        if (isShow) {
            HTMLElement.prototype.setAttribute.call(this, qualifiedName, value);
        }

        if (qualifiedName === 'value' && shadowElement) {
            this.value = shadowElement.getAttribute(qualifiedName);
        } else {
            this.$attrChange(qualifiedName);
        }
    }

    getAttribute(qualifiedName: string): string | null {
        const shadowElement = this[AuiComponent.shadowSymbol].el;
        return shadowElement ? shadowElement.getAttribute(qualifiedName) : super.getAttribute(qualifiedName);
    }

    removeAttribute(qualifiedName: string): void {
        const shadowElement = this[AuiComponent.shadowSymbol].el;
        if (shadowElement) {
            shadowElement.removeAttribute(qualifiedName);
        }
        const isShow = this.$isShowProp(_M.vueAttrName(qualifiedName));
        if (isShow) {
            HTMLElement.prototype.removeAttribute.call(this, qualifiedName);
        }
        this.$attrChange(qualifiedName);
    }

    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
        const shadowElement = this[AuiComponent.shadowSymbol].el;
        if (!shadowElement) return;
        if (oldValue === newValue) return;

        if (!this.$isShowProp(_M.vueAttrName(name))) {
            HTMLElement.prototype.removeAttribute.call(this, name);
        }

        shadowElement.setAttribute(name, newValue || '');
        if (name !== 'value') {
            this.$attrChange(name);
        }

    }

    // ---------------------------

    static $anyToArray(s: any): any {
        let arr = [];
        if (typeof s === 'string') {
            try {
                const json = JSON.parse(s);
                if (json instanceof Array) {
                    return json;
                }
            } catch (e) {
                console.log('$anyToArray err', e);
            }
            const sp = s.split(',');
            arr = sp.filter((item) => item ? item.trim() : false);
        } else if (s instanceof Array) {
            arr = s;
        }

        return arr;
    }

    // ---------------------------

    $resetShadowSymbol() {
        this[AuiComponent.shadowSymbol] = { needHook: false, slot: {} };
    }

    $customEvent(name: string, opts?: any) {
        this.dispatchEvent(new CustomEvent(name, Object.assign({
            bubbles: false,
            composed: false,
            cancelable: true,
        }, opts || {})));
    }

    $forceUpdate() {
        let slotCollection: Record<string, any> | null = null;
        const slot = this[AuiComponent.shadowSymbol].slot;
        for (const name in slot) {
            const children = this.$slotCollection(slot[name]);
            if (!slotCollection) slotCollection = {};
            slotCollection[name] = children;
        }

        if (slotCollection) {
            this[AuiComponent.shadowSymbol].slotCollection = slotCollection;
        }

        this.$customEvent('aui-force-update');
    }

    $slotCollection(cache: AUI_SLOT_CACHE) {
        const { start, end } = cache;
        const children = [];
        let child;
        while (start && (child = start.nextSibling) && child !== end) {
            start.parentNode?.removeChild(child);
            children.push(child);
        }
        return children;
    }

    $slotReplace() {
        const slotCollection = this[AuiComponent.shadowSymbol].slotCollection;
        if (!slotCollection) return;
        delete this[AuiComponent.shadowSymbol].slotCollection;
        for (const name in slotCollection) {
            const slot = this.querySelector(`aui-slot[name="${name}"]`) as AuiSlot;
            slot.$replace(this);
            this.$replaceChildren(name, slotCollection[name]);
        }
    }

    $textContent(name: string, ...args: any): string {
        if (args.length === 0) {
            const ret = [];
            const { start, end } = this.$slot(name) || {};
            let next: Comment | Node | ChildNode | null | undefined = start;
            while (next && (next = next.nextSibling) && next !== end) {
                ret.push(next.textContent);
            }
            return ret.join('');

        } else {
            if (!this.$hasSlot()) return '';
            this.$replaceChildren(name, [document.createTextNode(args[0] || '')]);
        }
        return '';
    }

    $innerHTML(name: string, ...args: any): string {
        if (args.length === 0) {
            const ret = [];
            const { start, end } = this.$slot(name) || {};
            let next: Comment | Node | ChildNode | null | undefined = start;
            while (next && (next = next.nextSibling) && next !== end) {
                if ((next as any).outerHTML) {
                    ret.push((next as any).outerHTML);
                } else {
                    let text = next.textContent;
                    if (next instanceof Comment) {
                        text = `<!--${text}-->`;
                    }
                    ret.push(text);
                }

            }
            return ret.join('');

        } else {
            if (!this.$hasSlot()) return '';
            const tpl = document.createElement('div');
            tpl.innerHTML = args[0] || '';
            const children = this.$moveChildren(tpl as any);
            this.$replaceChildren(name, children.default);
        }
        return '';
    }

    $prepend<T extends Node>(name: string, node: T) {
        const { start } = this.$slot(name) || {};
        start?.parentNode?.insertBefore(node, start.nextSibling);
    }

    $appendChild<T extends Node>(name: string, node: T): T {
        const { end } = this.$slot(name) || {};
        end?.parentNode?.insertBefore(node, end);
        return node;
    }

    $insertBefore<T extends Node>(name: string, node: T, ref: Node | null): T {
        const { start } = this.$slot(name) || {};
        const parentNode = start?.parentNode || this;
        if (ref) {
            if (parentNode === this) {
                HTMLElement.prototype.insertBefore.call(this, node, ref);
            } else {
                parentNode.insertBefore(node, ref);
            }
        } else {
            this.appendChild(node);
        }

        return node;
    }

    $childNodes(name?: string): NodeListOf<ChildNode> {
        const ret = [];
        const { start, end } = this.$slot(name) || {};
        let next: Comment | Node | ChildNode | null | undefined = start;
        while (next && (next = next.nextSibling) && next !== end) {
            ret.push(next);
        }
        return ret as any;
    }

    $children(name?: string): HTMLCollection {
        const ret = [];
        const { start, end } = this.$slot(name) || {};
        let next: Comment | Node | ChildNode | null | undefined = start;
        while (next && (next = next.nextSibling) && next !== end) {
            if (next instanceof HTMLElement) {
                ret.push(next);
            }
        }
        return ret as any;
    }

    $replaceChildren(name: string, children: any[]) {
        const { start, end } = this.$slot(name) || {};
        let next: Comment | Node | ChildNode | null | undefined = null;
        while (start && (next = start.nextSibling) && next !== end) {
            next.parentNode?.removeChild(next);
        }
        if (end) {
            while ((next = children.shift())) {
                end.parentNode?.insertBefore(next, end);
            }
        }
    }

    $slot(name?: string, slot?: AUI_SLOT_CACHE): AUI_SLOT_CACHE | undefined {
        name = name || 'default';
        if (slot) {
            this[AuiComponent.shadowSymbol].slot[name] = slot;
        } else {
            return this[AuiComponent.shadowSymbol].slot[name];
        }
    }

    $hasSlot() {
        const keys = Object.keys(this[AuiComponent.shadowSymbol].slot);
        return keys.length > 0;
    }

    $needHook(need?: boolean): boolean {
        if (typeof need === 'boolean') {
            this[AuiComponent.shadowSymbol].needHook = need;
        }
        return this[AuiComponent.shadowSymbol].needHook;
    }

    $ignoreLog(name: string) {
        console.warn(`aui component ignore : ${name}`);
    }

    $moveChildren(el?: AuiComponent): SlotElement {
        el = el || this;
        const list = el.childNodes;
        const ret: SlotElement = {
            default: []
        };
        while (list.length > 0) {
            const child = list[0] as AuiSlot;
            child.parentNode?.removeChild(child);
            if (AuiSlot.$isForAuiSlot(child)) {
                const name = child.getAttribute('for') || 'default';
                ret[name] = ret[name] || [];
                const children = Array.prototype.slice.call(child.childNodes);
                AuiSlot.prototype.$bind.call(child, el);
                ret[name].push(...children);
            } else {
                ret['default'].push(child);
            }
        }
        return ret;
    }

    $isArrayValue() {
        return this[AuiComponent.shadowSymbol].options?.valueType === Array;
    }

    $attrChange(name: string) {
        if (name === 'value') {
            const val = this.value;
            if (HTMLElement.prototype.hasAttribute.call(this, AUI_ATTR_FLAG)) {
                this.$call('$emit', `update:value`, val);
            } else {
                this.$customEvent('input', {
                    bubbles: true,
                    composed: true,
                });
            }
        }
        this.$customEvent('aui-attr-change', {
            bubbles: true,
            composed: true,
            detail: { attributeName: _M.vueAttrName(name) }
        });
    }

    $initAttrs() {
        const attributes = this.attributes;
        const shadow = document.createElement('div');
        for (let i = 0; i < attributes.length; i++) {
            const attr = attributes[i];
            shadow.setAttribute(attr.name, attr.value);
            if (!this.$isShowProp(_M.vueAttrName(attr.name))) {
                HTMLElement.prototype.removeAttribute.call(this, attr.name);
            }
            if (attr.name === 'value') {
                this.value = attr.value;
            }
        }
        Object.defineProperty(this, 'attributes', {
            get value() {
                return shadow.attributes;
            },
            writable: false,
        });

        this[AuiComponent.shadowSymbol].el = shadow;
    }

    $isShowProp(name: string): boolean {
        if (_M.isVueAttr(name)) return true;
        const prop = this.$getProp(name);
        // console.log('name:', name)
        const isShow = prop?.show;
        return isShow;
    }

    $getProp(name: string): any {
        const auiVueDefined = _M.cache.getAuiVueDefined(this.tagName);
        const props = (auiVueDefined?.origin as any)?.props || {};
        return props[name];
    }

    $initVue() {
        if (this.__hasVue__) {
            return;
        }
        this.__hasVue__ = true;

        const tagName = this.tagName.toLowerCase();
        const auiVueDefined = _M.cache.getAuiVueDefined(tagName);
        if (!auiVueDefined) {
            console.warn(`AUI组件 [${this.tagName}] 未定义`);
            return;
        }
        createApp(_M.createVueComponent(tagName, this)).mount(this);
    }

    $needInit() {
        const flag = HTMLElement.prototype.hasAttribute.call(this, AUI_ATTR_FLAG);
        if (flag) {
            return false;
        }

        return true;
    }

    $bindVue(v: Component) {
        if (this[AuiComponent.shadowSymbol].ref) {
            return;
        }
        // Object.assign(v, { $aui: this });
        this[AuiComponent.shadowSymbol].ref = v;
        this.__hasVue__ = true;
        // 触发init事件
        this.$customEvent('aui-init', {
            bubbles: false,
            detail: { code: 0 }
        });
        const auiInit = (_M.cache.getAuiVueDefined(this.tagName)?.origin as any)?.auiInit;
        if (auiInit) {
            auiInit.call(v);
        }
    }

    $call(name?: string, ...args: any[]): any {
        const ref = this[AuiComponent.shadowSymbol].ref as Record<string, any>;
        return _M.$call(ref, name, ...args);
    }

}

type TypeAuiComponent = typeof AuiComponent;

type AUI_VUE_DEFINE_CACHE = {
    aui?: TypeAuiComponent,
    vue?: Component,
    origin?: Component,
    hooks?: HookHandler[],
}


export class AuiSlot extends HTMLElement {

    _el_?: AuiComponent;
    _name_?: string;

    constructor() {
        super();
    }

    connectedCallback() {
        this.style.display = 'none';
    }

    set textContent(v: string) {
        if (this._name_) {
            this._el_?.$textContent(this._name_, v);
        } else {
            // super.textContent = v;
            Reflect.set(HTMLElement.prototype, 'textContent', v, this);
        }
    }

    set innerHTML(v: string) {
        if (this._name_) {
            this._el_?.$innerHTML(this._name_, v);
        } else {
            // super.innerHTML = v;
            Reflect.set(HTMLElement.prototype, 'innerHTML', v, this);
        }
    }

    prepend<T extends Node>(node: T): void {
        if (this._name_) {
            this._el_?.$prepend(this._name_, node);
        } else {
            HTMLElement.prototype.prepend.call(this, node);
        }
    }

    appendChild<T extends Node>(node: T): T {
        if (this._name_) {
            this._el_?.$appendChild(this._name_, node);
            return node;
        } else {
            return HTMLElement.prototype.appendChild.call(this, node) as T;
        }
    }

    // ---------------------

    static $isAuiSlot(el: HTMLElement) {
        if (!el || el.nodeType !== Node.ELEMENT_NODE) return false;

        if (el.tagName.toLowerCase() === 'aui-slot') return true;

        return false;
    }

    static $isForAuiSlot(el: HTMLElement) {
        if (AuiSlot.$isAuiSlot(el) && el.hasAttribute('for')) return true;
        return false;
    }

    $bind(el: AuiComponent) {
        this._el_ = el;
        this._name_ = this.getAttribute('for') || 'default';
    }

    $replace(el: AuiComponent) {
        const name = this.getAttribute('name') || 'default';
        const start = document.createComment(`aui-slot-${name}-start`);
        const end = document.createComment(`aui-slot-${name}-end`);

        el.$slot(name, { start, end });
        // this.replaceWith(start, end);
        this.$replaceWith(start, end);
    }

    $replaceWith(...args: any[]) {
        args.forEach((target) => {
            this.parentNode?.insertBefore(target, this);
        });
        this.parentNode?.removeChild(this);
    }
}

const _M = {
    init() {
        _M.defineSlot();
        return _M;
    },
    defineSlot() {
        _M.defineCustomElement('aui-slot', AuiSlot);
    },
    $call(ref: Record<string, any>, name?: string, ...args: any[]): any {

        if (!ref) {
            return new Error('not init');
        }

        if (!name) return ref;

        if (typeof ref[name] === 'function') {
            return ref[name].call(ref, ...args);
        }

        const sp = name.split('.');
        let target = ref, cur = null;
        const last = sp.pop() || '';

        while ((cur = sp.shift())) {
            if (Object.prototype.hasOwnProperty.call(target, cur)) {
                target = target[cur];
            } else {
                return new Error('no match name');
            }
        }

        if (args.length === 0) {
            return target[last];
        } else {
            target[last] = args[0];
            return null;
        }

    },
    formatName(name: string | undefined): string {
        if (!name) {
            return '';
        }
        name = name.replace(/[A-Z]/g, function (s) {
            return '-' + s.toLowerCase();
        });
        return TAG_PREFIX + name;
    },
    cache: {
        defined: {} as Record<string, AUI_VUE_DEFINE_CACHE>, // getGlobalAuiCore().defined,
        getAuiVueDefined(tagName: string): AUI_VUE_DEFINE_CACHE | undefined {
            tagName = tagName.toLowerCase();
            return _M.cache.defined[tagName];
        },
        getAuiPropNames(tagName: string): string[] {
            const auiVueDefined = _M.cache.getAuiVueDefined(tagName);
            const props = (auiVueDefined?.origin as any)?.props || {};
            const names = [];
            for (const key in props) {
                names.push(_M.auiAttrName(key));
            }
            if (names.indexOf(AUI_EXTEND_ATTR) === -1) {
                names.push(AUI_EXTEND_ATTR);
            }
            return names;
        },
        addHooks(name: string, handle: HookHandler) {
            name = name.toLowerCase();
            const auiVueDefined = _M.cache.getAuiVueDefined(name);
            if (auiVueDefined) {
                auiVueDefined.hooks = auiVueDefined.hooks || [];
                auiVueDefined.hooks.push(handle);
            } else {
                _M.cache.defined[name] = {
                    hooks: [handle],
                }
            }
        }
    },
    createExtendComp(v: Component): Component {
        return defineComponent({
            props: {
                [AUI_EXTEND_ATTR]: {
                    type: String,
                },
                value: {
                    type: (v as any).props?.value?.type || [String, Number, Array],
                }
            },
            methods: {
                $anyToArray: AuiComponent.$anyToArray,
                $value(...args: any[]) {
                    const $aui = this.$aui();
                    if (args.length === 0) {
                        return $aui?.value;
                    } else {
                        if ($aui) {
                            $aui.value = args[0];
                        }
                    }
                },
                $call(name?: string, ...args: any[]): any {
                    const $aui = this.$aui();
                    if (!$aui) return null;
                    return _M.$call($aui, name, ...args);
                },
                $aui(): AuiComponent | null {
                    return this.$el?.parentNode;
                },
            },
            data() {
                return {

                }
            }
        })
    },
    patchDefaultProps(origin: Component): any {

        const valueType = (origin as any).props?.value?.type;

        if (!(origin as any).mixins) {
            (origin as any).mixins = [];
        }

        (origin as any).mixins.push(_M.createExtendComp(origin));

        return valueType;

    },
    defineAuiComponent(tagName: string, component: Component, origin: Component) {

        const valueType = _M.patchDefaultProps(origin);

        if (!_M.cache.defined[tagName]?.origin) {
            class AuiChild extends AuiComponent {
                constructor() {
                    super({ valueType });
                }

                static get observedAttributes() {
                    return _M.cache.getAuiPropNames(tagName);
                }

            }

            _M.cache.defined[tagName] = {
                aui: AuiChild,
                vue: component,
                origin,
                hooks: _M.cache.defined[tagName]?.hooks || [],
            };

            _M.defineCustomElement(tagName, AuiChild);

        } else {
            _M.cache.defined[tagName].origin = origin;
        }
    },

    defineCustomElement(tagName: string, cls: any) {
        try {
            customElements.define(tagName, cls);
        } catch (_) {
            // console.warn('defineCustomElement error', error);
        }
    },

    sameComponent(tagName: string, v: any): boolean {
        if (!Object.prototype.hasOwnProperty.call(v, '__hmrId')) return false;
        const auiVueDefined = _M.cache.getAuiVueDefined(tagName);
        const origin = auiVueDefined?.origin as any;
        if (!origin) return false;
        return v.__hmrId === origin.__hmrId;
    },

    notifyComponentUpdate(tagName: string) {
        const comps = document.querySelectorAll(tagName) as NodeListOf<AuiComponent>;
        comps.forEach((comp) => {
            comp.$forceUpdate();
        });
    },

    define(v: Component, force = true): Component {
        const tagName = _M.formatName(v.name);

        // console.log('define aui comp, name is', tagName);

        if (!tagName) {
            console.warn('当前组件不符合AUI定义');
            return v;
        }

        const auiVueDefined = _M.cache.getAuiVueDefined(tagName);

        if (auiVueDefined && auiVueDefined.vue) {
            if (force || _M.sameComponent(tagName, v)) {
                console.log('aui组件更新origin定义');
                _M.defineAuiComponent(tagName, auiVueDefined.vue, v);
                _M.notifyComponentUpdate(tagName);
            } else {
                console.warn('AUI组件已经被定义，当前组件将被舍弃，将返回已定义的组件，如需覆盖组件，请添加force: true');
            }

            return auiVueDefined.vue;
        }

        const wrappedComponent = _M.createVueComponent(tagName);

        _M.defineAuiComponent(tagName, wrappedComponent, v);

        return wrappedComponent;
    },
    createTempSlot(children: SlotElement): any {
        const ret = {} as { [key: string]: any };
        Object.keys(children).forEach((name) => {
            ret[name] = () => h('aui-slot', { name });
        });
        return ret;
    },
    replaceTempSlot(el: AuiComponent, children: SlotElement) {
        const names = Object.keys(children);
        names.forEach((name) => {
            const slot = el.querySelector(`aui-slot[name="${name}"]`) as AuiSlot | null;
            if (slot) {
                AuiSlot.prototype.$replace.call(slot, el);
                el.$replaceChildren(name, children[name]);
            }
        });

    },
    createVueComponent(tagName: string, el?: AuiComponent): Component {

        const children = el ? el.$moveChildren() : { default: [] };

        const wrappedComponent = {
            setup() {
                const attrs = useAttrs()
                const vueComponentRef = shallowRef<any>(null)
                const elementRef = shallowRef<AuiComponent | null>(null)
                const selfAttrs = ref<Record<string, any>>({})
                const slotsRef = shallowRef<any>(el ? _M.createTempSlot(children) : useSlots())
                const target = shallowRef<Component | null>(null);

                onMounted(() => {
                    if (vueComponentRef.value && elementRef.value && elementRef.value.$bindVue) {
                        elementRef.value.$bindVue(vueComponentRef.value);

                        el && _M.replaceTempSlot(el, children);

                        // 监听属性变化事件
                        elementRef.value.addEventListener('aui-attr-change', () => {
                            if (elementRef.value) {
                                selfAttrs.value = _M.getAuiAttrs(elementRef.value);
                            }
                        });

                        // 监听自定义强制更新事件
                        elementRef.value.addEventListener('aui-force-update', () => {
                            target.value = null;
                        });

                        elementRef.value.$call('$emit', 'update:value', elementRef.value.value);
                    } else {
                        console.warn('AUI组件绑定失败');
                    }
                })

                onUpdated(() => {
                    if (elementRef.value) {
                        elementRef.value.$slotReplace();
                    }
                })

                return () => {

                    if (el) {
                        elementRef.value = el;
                        selfAttrs.value = _M.getAuiAttrs(el);
                    }

                    if (!target.value) {
                        (target as any).value = _M.formatStructure(tagName);
                    }

                    if (!target.value) {
                        console.warn('AUI组件未定义，加载失败，name：', tagName);
                        return null;
                    }

                    const trueComp = h(target.value, { ...attrs, ...selfAttrs.value, ref: vueComponentRef }, slotsRef.value || {});

                    const ret = el ? trueComp : h(tagName, { ref: elementRef, [AUI_ATTR_FLAG]: '' }, [
                        trueComp
                    ]);

                    return ret;
                }
            }
        };

        return wrappedComponent;
    },
    getAuiAttrs(el: AuiComponent): Record<string, any> {

        const attrs = {} as Record<string, any>;

        const attributes = el.attributes;

        for (let i = 0; i < attributes.length; i++) {
            const attr = attributes[i];
            // 跳过 aui 和 vue 标记属性
            if (attr.name === TAG_PREFIX || _M.isVueAttr(attr.name)) continue;
            attrs[attr.name] = attr.value;
        }

        if (el.value !== null) {
            attrs.value = el.value;
        }

        return attrs;
    },
    isVueAttr(name: string): boolean {
        return name.indexOf('data-v-') === 0;
    },
    auiAttrName(name: string): string {
        return name.replace(/([A-Z])/g, (a) => '-' + a.toLowerCase());
    },
    vueAttrName(name: string): string {
        if (_M.isVueAttr(name)) return name;
        return name.replace(/-([a-z])/g, (_, s1) => s1.toUpperCase());
    },
    hookStructure(name: string, handle: HookHandler) {
        _M.cache.addHooks(name, handle);
    },
    getHookStructure(name: string): HookHandler[] {
        const auiVueDefined = _M.cache.getAuiVueDefined(name);
        const hooks = auiVueDefined?.hooks || [];
        return hooks.slice(0);
    },
    formatStructure(tagName: string): Component | null {
        const auiVueDefined = _M.cache.getAuiVueDefined(tagName);
        if (!auiVueDefined?.origin) {
            return null;
        }
        let origin = merge({}, auiVueDefined.origin) as Component;
        const hooks = _M.getHookStructure(tagName);
        let handle: HookHandler | null | undefined;
        while ((handle = hooks.shift())) {
            const cur = handle(origin);
            if (cur) {
                origin = cur;
            }
        }

        return origin;
    }
};

export default _M.init();